import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { toast } from "sonner";
import {
  Camera,
  X,
  ArrowRight,
  ArrowLeft,
  ImagePlus,
  Info,
} from "lucide-react";
import { Header, Footer } from "@/components/site/Chrome";
import ProgressSteps from "@/components/site/ProgressSteps";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  computeEstimate,
  loadIntake,
  saveIntake,
  SCOPE_LABELS,
  FREQUENCY_LABELS,
  FALLBACK_CONFIG,
} from "@/lib/intake";
import { fetchEstimateConfig } from "@/lib/api";

const MAX_PHOTOS = 4;
const MAX_SOURCE_BYTES = 5 * 1024 * 1024; // 5MB source-file cap (pre-compression)
const TARGET_MAX_BYTES = 900 * 1024; // trigger second compression pass above this
const HARD_MAX_BYTES = 950 * 1024;   // give up above this — reject the photo
const STEP_LABELS = ["Photos", "Scope", "Frequency"];

export default function Estimate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);
  const [compressing, setCompressing] = useState(false);

  const [photos, setPhotos] = useState([]); // [{ filename, content_type, size, data_url }]
  const [scope, setScope] = useState({
    sidewalk: false,
    deck: false,
    furniture: false,
    other: false,
    other_text: "",
  });
  const [frequency, setFrequency] = useState("");
  const [estimateConfig, setEstimateConfig] = useState(FALLBACK_CONFIG);

  // Fetch live config so estimate math reflects admin settings.
  useEffect(() => {
    let alive = true;
    fetchEstimateConfig()
      .then((cfg) => alive && setEstimateConfig(cfg))
      .catch(() => {
        /* fallback stays */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Hydrate from prior session
  useEffect(() => {
    const prev = loadIntake();
    if (prev) {
      if (prev.photos) setPhotos(prev.photos);
      if (prev.scope) setScope(prev.scope);
      if (prev.frequency) setFrequency(prev.frequency);
    }
  }, []);

  // Persist state on any change
  useEffect(() => {
    saveIntake({ photos, scope, frequency });
  }, [photos, scope, frequency]);

  async function handleFiles(files) {
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    setCompressing(true);
    const next = [...photos];
    for (const file of list) {
      if (!file.type.startsWith("image/")) {
        toast.error(`"${file.name}" is not an image`);
        continue;
      }
      if (file.size > MAX_SOURCE_BYTES) {
        toast.error("That photo is too big — please pick one under 5MB.");
        continue;
      }
      try {
        // Primary pass — aims well under the 1MB server ceiling.
        let out = await imageCompression(file, {
          maxSizeMB: 0.9,
          maxWidthOrHeight: 1600,
          initialQuality: 0.8,
          useWebWorker: true,
          fileType: "image/jpeg",
        });
        // Guard pass for oddly-compressible images (HDR sky, glare, sand).
        if (out.size > TARGET_MAX_BYTES) {
          console.log(
            `[gs] guard-pass on "${file.name}" (primary=${Math.round(out.size / 1024)}KB)`
          );
          out = await imageCompression(out, {
            maxSizeMB: 0.6,
            maxWidthOrHeight: 1400,
            initialQuality: 0.6,
            useWebWorker: true,
            fileType: "image/jpeg",
          });
        }
        console.log(
          `[gs] compressed "${file.name}": ${Math.round(file.size / 1024)}KB → ${Math.round(out.size / 1024)}KB`
        );
        if (out.size > HARD_MAX_BYTES) {
          toast.error(
            "Couldn't compress this photo enough. Try a different one or crop it first."
          );
          continue;
        }
        const dataUrl = await imageCompression.getDataUrlFromFile(out);
        next.push({
          filename: file.name,
          content_type: "image/jpeg",
          size: out.size,
          data_url: dataUrl,
        });
      } catch (err) {
        console.error(err);
        toast.error(`Could not process "${file.name}"`);
      }
    }
    setPhotos(next);
    setCompressing(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removePhoto(i) {
    setPhotos(photos.filter((_, idx) => idx !== i));
  }

  const scopeSelected =
    scope.sidewalk || scope.deck || scope.furniture || scope.other;
  const otherValid = !scope.other || scope.other_text.trim().length > 0;

  const canNext = {
    1: photos.length >= 1 && !compressing,
    2: scopeSelected && otherValid,
    3: !!frequency,
  }[step];

  function onNext() {
    if (!canNext) return;
    if (step < 3) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      // Move to summary — same page, step 4
      setStep(4);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }

  function onBack() {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }

  const estimate = computeEstimate(scope, estimateConfig);

  return (
    <>
      <Header />
      <main className="pt-20 pb-16 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {step <= 3 && (
            <div className="mb-6">
              <ProgressSteps current={step} labels={STEP_LABELS} />
            </div>
          )}

          {/* STEP 1: Photos */}
          {step === 1 && (
            <section data-testid="step-photos">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#334E68] tracking-tight">
                Show us what needs cleaning
              </h1>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                Upload up to 4 photos (max 5MB each). We'll compress them so
                uploads are fast on cellular.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                data-testid="photo-file-input"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />

              <button
                type="button"
                data-testid="photo-add-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={photos.length >= MAX_PHOTOS || compressing}
                className="mt-6 w-full min-h-[160px] border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex flex-col items-center justify-center gap-2 text-[#334E68]"
              >
                {compressing ? (
                  <>
                    <div className="w-6 h-6 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm font-medium">Optimizing…</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-[#16A34A]" />
                    <span className="text-base font-semibold">
                      Tap to add photos
                    </span>
                    <span className="text-xs text-gray-500">
                      {photos.length}/{MAX_PHOTOS} added · we'll compress for fast upload
                    </span>
                  </>
                )}
              </button>

              {photos.length > 0 && (
                <div
                  data-testid="photo-thumbnails"
                  className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3"
                >
                  {photos.map((p, i) => (
                    <div
                      key={i}
                      data-testid={`photo-thumb-${i}`}
                      className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-100"
                    >
                      <img
                        src={p.data_url}
                        alt={p.filename}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        data-testid={`photo-remove-${i}`}
                        onClick={() => removePhoto(i)}
                        aria-label={`Remove photo ${i + 1}`}
                        className="absolute top-1.5 right-1.5 w-8 h-8 flex items-center justify-center bg-white/95 backdrop-blur text-gray-700 hover:text-red-600 rounded-full shadow-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && !compressing && (
                    <button
                      type="button"
                      data-testid="photo-add-more"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                    >
                      <ImagePlus className="w-6 h-6" />
                    </button>
                  )}
                </div>
              )}
            </section>
          )}

          {/* STEP 2: Scope */}
          {step === 2 && (
            <section data-testid="step-scope">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#334E68] tracking-tight">
                What are we cleaning?
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Select all that apply.
              </p>

              <div className="mt-6 space-y-3">
                {Object.entries(SCOPE_LABELS).map(([key, label]) => {
                  const checked = scope[key];
                  return (
                    <label
                      key={key}
                      data-testid={`scope-tile-${key}`}
                      htmlFor={`scope-${key}`}
                      className={[
                        "flex items-center gap-4 p-4 min-h-[64px] bg-white border-2 rounded-2xl cursor-pointer transition-colors",
                        checked
                          ? "border-[#16A34A] bg-green-50/50"
                          : "border-gray-200 hover:border-gray-300",
                      ].join(" ")}
                    >
                      <Checkbox
                        id={`scope-${key}`}
                        data-testid={`scope-checkbox-${key}`}
                        checked={checked}
                        onCheckedChange={(v) =>
                          setScope((s) => ({ ...s, [key]: !!v }))
                        }
                        className="w-6 h-6 border-2 data-[state=checked]:bg-[#16A34A] data-[state=checked]:border-[#16A34A]"
                      />
                      <span className="text-base font-medium text-[#334E68]">
                        {label}
                      </span>
                    </label>
                  );
                })}
              </div>

              {scope.other && (
                <div className="mt-4">
                  <Label
                    htmlFor="scope-other-text"
                    className="text-sm font-medium text-[#334E68]"
                  >
                    Describe "Other"
                  </Label>
                  <Input
                    id="scope-other-text"
                    data-testid="scope-other-text"
                    value={scope.other_text}
                    onChange={(e) =>
                      setScope((s) => ({ ...s, other_text: e.target.value }))
                    }
                    placeholder="e.g., driveway, fence, siding"
                    className="mt-2 min-h-[52px] rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
                  />
                </div>
              )}
            </section>
          )}

          {/* STEP 3: Frequency */}
          {step === 3 && (
            <section data-testid="step-frequency">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#334E68] tracking-tight">
                How often?
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Choose the cadence that fits your home.
              </p>

              <RadioGroup
                value={frequency}
                onValueChange={setFrequency}
                className="mt-6 space-y-3"
              >
                {Object.entries(FREQUENCY_LABELS).map(([key, label]) => {
                  const checked = frequency === key;
                  return (
                    <label
                      key={key}
                      data-testid={`frequency-tile-${key}`}
                      htmlFor={`freq-${key}`}
                      className={[
                        "flex items-center gap-4 p-4 min-h-[64px] bg-white border-2 rounded-2xl cursor-pointer transition-colors",
                        checked
                          ? "border-[#16A34A] bg-green-50/50"
                          : "border-gray-200 hover:border-gray-300",
                      ].join(" ")}
                    >
                      <RadioGroupItem
                        id={`freq-${key}`}
                        value={key}
                        data-testid={`frequency-radio-${key}`}
                        className="w-5 h-5 border-2 text-[#16A34A]"
                      />
                      <span className="text-base font-medium text-[#334E68]">
                        {label}
                      </span>
                    </label>
                  );
                })}
              </RadioGroup>
            </section>
          )}

          {/* STEP 4: Summary */}
          {step === 4 && (
            <section data-testid="step-summary">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#334E68] tracking-tight">
                Your Estimate
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Based on what you shared, here's the starting range.
              </p>

              <div
                data-testid="estimate-summary-card"
                className="mt-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm"
              >
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Your selections
                </div>
                <ul className="mt-3 space-y-1.5 text-sm text-gray-700">
                  <li data-testid="summary-photo-count">
                    <span className="text-gray-500">Photos:</span>{" "}
                    <span className="font-medium text-[#334E68]">
                      {photos.length}
                    </span>
                  </li>
                  <li data-testid="summary-scope-list">
                    <span className="text-gray-500">Scope:</span>{" "}
                    <span className="font-medium text-[#334E68]">
                      {Object.entries(SCOPE_LABELS)
                        .filter(([k]) => scope[k])
                        .map(([k, v]) =>
                          k === "other" && scope.other_text
                            ? `${v} (${scope.other_text})`
                            : v
                        )
                        .join(", ") || "—"}
                    </span>
                  </li>
                  <li data-testid="summary-frequency">
                    <span className="text-gray-500">Frequency:</span>{" "}
                    <span className="font-medium text-[#334E68]">
                      {FREQUENCY_LABELS[frequency] || "—"}
                    </span>
                  </li>
                </ul>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                    Estimated Starting At
                  </div>
                  <div
                    data-testid="estimate-price-range"
                    className="mt-1 font-display text-4xl sm:text-5xl font-bold text-[#334E68]"
                  >
                    ${estimate.low} – ${estimate.high}
                  </div>
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500 leading-relaxed">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      Final price determined on-site after Charlie reviews your
                      photos.
                    </span>
                  </p>
                </div>
              </div>

              <button
                type="button"
                data-testid="continue-to-booking-cta"
                onClick={() => navigate("/book")}
                className="mt-6 w-full min-h-[56px] bg-[#16A34A] hover:bg-[#15803D] text-white rounded-2xl font-semibold text-base tracking-wide uppercase flex items-center justify-center gap-2 transition-colors"
              >
                Continue to Booking
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="summary-back-button"
                onClick={() => setStep(3)}
                className="mt-3 w-full min-h-[48px] text-sm text-gray-600 hover:text-[#334E68] flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back to edit
              </button>
            </section>
          )}

          {/* Nav row (only for steps 1-3) */}
          {step <= 3 && (
            <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
              <button
                type="button"
                data-testid="step-back-button"
                onClick={onBack}
                disabled={step === 1}
                className="min-h-[52px] px-6 rounded-2xl text-sm font-medium text-[#334E68] hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="button"
                data-testid="step-next-button"
                onClick={onNext}
                disabled={!canNext}
                className="w-full sm:w-auto sm:min-w-[220px] min-h-[56px] px-8 bg-[#16A34A] hover:bg-[#15803D] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-2xl font-semibold text-base tracking-wide uppercase transition-colors flex items-center justify-center gap-2"
              >
                {step < 3 ? "Continue" : "See Estimate"}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
